import { i18n } from '@/plugins/i18n'
import PlanService from '@/services/plan.service'
import {
  ApiError,
} from '@/services/models'
import PlanRsp from '@/services/models/PlanRsp'
import { IApiResponse } from '@/common/models/interface'
import _ from 'lodash'
import { PaymentOption } from '@/services/models/Plan'
import PlanMonthlyInfo from '@/views/Subscription/models/PlanMonthlyInfo'
import { StatusCodes } from 'http-status-codes'
import { InUseSubscriptionsRsp, SubscriptionProfileRsp } from '@/services/models/Member'
import MemberService from '@/services/member.service'
import SubscriptionService from '@/services/subscription.service'
import {
  RenewalPaymentOptionPatchParam,
  SubscriptionOrderCheckoutParam,
  SubscriptionOrderCheckoutRsp,
  SubscriptionParam,
  SubscriptionRsp,
} from '@/services/models/Subscription'
import { EnumPlanCategory, EnumPlanChangeType, EnumSubscriptionType } from '@/views/Subscription/enum'
import { EnumPaymentStatus } from '@/common/models/enum'

const plan = new PlanService()
const member = new MemberService()
const subscription = new SubscriptionService()

export default {
  state: {
    subscriptionRsp: {} as SubscriptionRsp,
    subscriptionOrderCheckoutRsp: {} as SubscriptionOrderCheckoutRsp,
    planListRsp: [] as PlanRsp[],
    planRsp: {} as PlanRsp,
    conflictOrder: '' as string,
    paymentOptions: [] as PaymentOption[],
    inUseSubscriptionsRsp: {} as InUseSubscriptionsRsp,
    allInUseSubscriptions: [] as InUseSubscriptionsRsp[],
    apiError: {} as ApiError,
    subscriptionProfile: {} as SubscriptionProfileRsp
  },

  getters: {
    SubscriptionRsp: (state: any) => state.subscriptionRsp,
    subscriptionOrderCheckoutRsp: (state: any) => state.subscriptionOrderCheckoutRsp,
    planListRsp: (state: any) => state.planListRsp,
    planRsp: (state: any) => state.planRsp,
    conflictOrder: (state: any) => state.conflictOrder,
    inUseSubscriptionsRsp: (state: any) => state.inUseSubscriptionsRsp,
    allInUseSubscriptions: (state: any) => state.allInUseSubscriptions,
    apiError: (state: any) => state.apiError,
    subscriptionProfile: (state: any) => state.subscriptionProfile,

    /*
    * retrieve payment selection information
    * */
    planMonthlyInfos: (state: any): PlanMonthlyInfo[] => {

      const planInfos: PlanMonthlyInfo[] = []
      _.forEach(state.planListRsp, (plan: PlanRsp) => {

        const planMonthlyInfo = new PlanMonthlyInfo({ ...plan })

        _.forEach(plan.paymentOptions, (paymentOption: PaymentOption) => {
          if (paymentOption.durationDays === 30) {
            planMonthlyInfo.price = paymentOption.price
          }
        })
        planInfos.push(planMonthlyInfo)
      })
      return planInfos.sort((a, b) => {
        return a.price - b.price
      })
    },

    /*
    * retrieve payment options for a plan, which is sorted by duration days
    * */
    getPaymentOptions: (state: any) => (planId: string): PaymentOption[] => {
      const plan: PlanRsp = _.find(state.planListRsp, (plan) => { return plan.id === planId })
      const paymentOptions: PaymentOption[] =
        plan.paymentOptions.sort((a: PaymentOption, b: PaymentOption) => {
          return b.durationDays - a.durationDays
        })

      return paymentOptions.reverse()
    },

    /*
    * retrieve payment options for a plan without order
    * */
    getPaymentOptionsDesc: (state: any) => (planId: string): PaymentOption[] => {
      const plan: PlanRsp = _.find(state.planListRsp, (plan) => { return plan.id === planId })
      const paymentOptions: PaymentOption[] =
        plan.paymentOptions.sort((a: PaymentOption, b: PaymentOption) => {
          return b.durationDays - a.durationDays
        })

      return paymentOptions
    },

    getSubscriptionType: (state: any): EnumSubscriptionType => {
      if (!state.subscriptionProfile.nextPaidSubscriptionId) {
        return EnumSubscriptionType.Post
      } else {
        return EnumSubscriptionType.Patch
      }
    },

    /*
    * retrieve the status of upgrade or downgrade from my current plan to targetPlanId
    * */
    getPlanChangeType: (state: any, getters: any) => (targetPlanId: string): EnumPlanChangeType => {
      if (!state.subscriptionProfile.currentPaidSubscriptionId) {
        return EnumPlanChangeType.FreshSubscription
      } else {
        if (targetPlanId === getters.getMyPlanId) {
          return EnumPlanChangeType.RenewalPlan
        }

        if (_.some(state.planRsp.upgradePlans, (plan: any) => {
          return plan?.id === targetPlanId
        }
        )) {
          return EnumPlanChangeType.UpgradePlan
        } else if (_.some(state.planRsp.downgradePlans, (plan: any) => {
          return plan?.id === targetPlanId
        }
        )) {
          return EnumPlanChangeType.DowngradePlan
        } else {
          return EnumPlanChangeType.RenewalPlan
        }
      }
    },

    /*
    * check if this plan is highest paid plan
    * */
    isHighestPaidSubscription: (state: any, getters: any) => (planId: string): boolean => {
      const highestPlan: PlanMonthlyInfo = getters.planMonthlyInfos[getters.planMonthlyInfos.length - 1]

      return planId === highestPlan.id
    },

    /*
    * retrieve member purchase plan string
    * */
    getChangePlanString: (state: any, getters: any) => (): string => {
      const highestPlan: PlanMonthlyInfo = getters.planMonthlyInfos[getters.planMonthlyInfos.length - 1]
      const isHighestPaidSubscription = getters.getMyPlanId === highestPlan.id
      const _t: any = i18n.global

      return isHighestPaidSubscription ? _t.t('action.change') : _t.t('action.upgrade')
    },

    /*
    * retrieve payment option label for a payment option
    * */
    getPaymentOptionLabel: (state: any, getters: any) => (paymentOption: PaymentOption): string => {
      const methodString = getters.getPaymentMethodLabel(paymentOption)
      const _t: any = i18n.global

      return methodString +
        ` (${paymentOption.durationDays} ${_t.t('subs.payDayPeriod')})`
    },

    /*
    * retrieve payment method string for a payment option
    * */
    getPaymentMethodLabel: () => (paymentOption: PaymentOption | undefined): string => {
      const _t: any = i18n.global
      let methodString = ''
      let labelString = ''
      switch (paymentOption?.method) {
      case undefined:
        return ''
      case 'VA':
        methodString = 'atm'
        labelString = _t.t(`subs.${methodString}`) + '/' + _t.t('subs.transferMoney')
        break
      case 'CC':
        methodString = 'creditCard'
        labelString = _t.t(`subs.${methodString}`)
        break
      case 'GOOGLE':
        methodString = 'googlePlay'
        labelString = _t.t(`subs.${methodString}`)
        break
      case 'APPLE':
        methodString = 'appStore'
        labelString = _t.t(`subs.${methodString}`)
        break
      default:
        break
      }
      return labelString
    },

    /*
    * retrieve a plan information by Id
    * */
    getPlan: (state: any) => (planId: string): PlanRsp => {
      return _.find(state.planListRsp, (plan) => { return plan.id === planId })
    },

    /*
    * retrieve user plan id
    * */
    getMyPlanId: (state: any) => {
      return state.inUseSubscriptionsRsp.planId
    },

    /*
    * retrieve user plan id
    * */
    getMySubscriptionDuration: (state: any) => {
      const dtStart = new Date(Date.parse(state.inUseSubscriptionsRsp.startOn))
      const dtEnd = new Date(Date.parse(state.inUseSubscriptionsRsp.endOn))
      return [dtStart.toLocaleDateString(), dtEnd.toLocaleDateString()]
    },

    /*
    * check current subscription status can do upgrade or not
    * */
    canDoChangeGrade: (state: any, getters: any) => {
      // In app purchase can't do upgrade
      if (state.inUseSubscriptionsRsp.plan?.category !== EnumPlanCategory.Preview &&
        (state.subscriptionProfile.currentPaidSubscription.paymentOption?.method === 'GOOGLE' ||
          state.subscriptionProfile.currentPaidSubscription.paymentOption?.method === 'APPLE')) {
        return false
      }

      // find if there's paid plan
      if (state.planRsp.upgradePlans.length === 0 && state.planRsp.downgradePlans.length === 0) {
        return false
      }

      const currentSubsEndOn = getters.getMySubscriptionDuration[1]

      if (state.inUseSubscriptionsRsp.plan?.category === EnumPlanCategory.Sales) {
        const diffDays = 1000 * 60 * 60 * 24 * 15  // 15 days
        const canUpgradeDate =
          new Date(Date.parse(currentSubsEndOn) - diffDays)
        const today = new Date()
        return today >= canUpgradeDate
      } else {
        if (state.subscriptionProfile.nextPaidSubscription) {
          return state.subscriptionProfile.nextPaidSubscription.status !== EnumPaymentStatus.Complete
        }
        return true
      }
    },

  },

  mutations: {
    setSubscription(state: any, subscriptionRsp: IApiResponse<SubscriptionRsp>) {
      state.subscriptionRsp = { ...subscriptionRsp.data }
      state.conflictOrder = ''
    },

    setConflictOrder(state: any, conflictOrder: string) {
      state.conflictOrder = conflictOrder
    },

    setCheckout(state: any, checkoutRsp: IApiResponse<SubscriptionOrderCheckoutRsp>) {
      state.subscriptionOrderCheckoutRsp = { ...checkoutRsp.data }
    },

    setPlan(state: any, planRsp: IApiResponse<PlanRsp>) {
      const plan = { ...planRsp.data }
      plan.paymentOptions = _.filter(plan.paymentOptions, item => item.method === 'VA' || item.method === 'CC')
      state.planRsp = plan
    },

    setPlanList(state: any, planListRsp: IApiResponse<PlanRsp[]>) {
      const planList = { ...planListRsp.data }
      _.forEach(planList, (plan: PlanRsp) => {
        plan.paymentOptions = _.filter(plan.paymentOptions, item => item.method === 'VA' || item.method === 'CC')
      })
      state.planListRsp = planList
    },

    setInUseSubscriptionsRsp(state: any, inUseSubscriptionsRsps: IApiResponse<InUseSubscriptionsRsp[]>) {
      /*
      * TODO: 這裡有可能有多種方案，還在討論要用甚麼來判斷作用中的 subscription
      * */
      const subsArray: InUseSubscriptionsRsp[] = inUseSubscriptionsRsps.data

      state.allInUseSubscriptions = JSON.parse(JSON.stringify(subsArray))

      /*
      * CYBSS-740: For gup migration check, so it is a special case via hard code.
      * */
      let showPlan4GupMigration = _.find(subsArray, (subs: InUseSubscriptionsRsp) => {
        return subs.plan?.id === '5c9f6315-c932-4fd9-b0b0-5f209a8e4c48'
      })
      if (showPlan4GupMigration !== undefined) {
        const hasPaidPlan = _.find(subsArray, (subs: InUseSubscriptionsRsp) => {
          return subs.plan?.category === 'Paid'
        })

        if (hasPaidPlan !== undefined) {
          showPlan4GupMigration = hasPaidPlan
        }
      }
      // End CYBSS-740

      const salesSubscription = _.find(subsArray, (subs: InUseSubscriptionsRsp) => {
        return subs.plan?.category === 'Sales'
      })

      const paidSubscription = _.find(subsArray, (subs: InUseSubscriptionsRsp) => {
        return subs.plan?.category === 'Paid'
      })

      if (showPlan4GupMigration !== undefined) {
        state.inUseSubscriptionsRsp = { ...showPlan4GupMigration }
      } else if (salesSubscription !== undefined) {
        state.inUseSubscriptionsRsp = { ...salesSubscription }
      } else if (paidSubscription !== undefined) {
        state.inUseSubscriptionsRsp = { ...paidSubscription }
      } else { // Preview plan
        const previewSubscription = _.find(subsArray, (subs: InUseSubscriptionsRsp) => {
          return subs.plan?.category === 'Preview'
        })
        state.inUseSubscriptionsRsp = { ...previewSubscription }
      }
    },

    setSubscriptionProfile(state: any, subscriptionProfile: IApiResponse<SubscriptionProfileRsp>) {
      state.subscriptionProfile = { ...subscriptionProfile.data }
    },
  },

  actions: {

    /**
    * post an order, and update state if atm payment create
    * */
    async postSubscription({ commit, dispatch }: { commit: any, state: any, dispatch: any },
      subscriptionParam: SubscriptionParam) {

      try {
        const res: any = await subscription.post(subscriptionParam)
        commit('setSubscription', (res.data) as IApiResponse<SubscriptionRsp>)
        await dispatch('getSubscriptionProfile')
        return res.status
      } catch (error: any) {
        let res: IApiResponse<SubscriptionRsp>

        if (error.response.status === StatusCodes.CONFLICT) {
          res = error.response.data as IApiResponse<SubscriptionRsp>
          commit('setConflictOrder', res.data.lastOrder?.number)
          return StatusCodes.CONFLICT
        } else if (error.response.status >= 500) {
          return error.response.status
        } else {
          return false
        }
      }
    },

    async checkoutOrder({ commit }: { commit: any, state: any },
      orderCheckoutParam: SubscriptionOrderCheckoutParam) {
      try {
        commit('setCheckout', await subscription.orderCheckout(orderCheckoutParam))
        return true
      } catch (error) {
        return false
      }
    },

    async getAllPlans({ commit }: { commit: any, state: any }) {
      commit('setPlanList', await plan.getAll())
    },

    async getMySubscriptionStatus({ commit, getters }: { commit: any, getters: any }) {
      commit('setInUseSubscriptionsRsp', await member.getInUseSubscriptions())
      commit('setPlan', await plan.getPlan(getters.inUseSubscriptionsRsp.planId))
    },

    async patchRenewalPaymentOptions({ dispatch }: { dispatch: any },
      renewalPaymentOptionPatchParam: RenewalPaymentOptionPatchParam) {

      try {
        await subscription.patchRenewalPaymentOption(renewalPaymentOptionPatchParam)
        await dispatch('getMySubscriptionStatus')
        return true
      } catch (error) {
        return false
      }
    },

    async cancelSubscription({ dispatch, getters }:
      { dispatch: any, getters: any }) {
      try {
        // Cancel next paid subscription
        const nextPaidSubs: SubscriptionProfileRsp = getters.subscriptionProfile
        if (nextPaidSubs.nextPaidSubscription) {
          await subscription.cancel()
          await dispatch('getMySubscriptionStatus')
        }
        await dispatch('getSubscriptionProfile')

        return true
      } catch (error) {
        return false
      }
    },

    async upgradeSubscription({ dispatch }: { dispatch: any }, upgradeParam: SubscriptionParam) {
      try {
        const resp: any = await subscription.patch(upgradeParam)
        const upgradeRsp: SubscriptionRsp = resp.data
        if (upgradeRsp.lastOrder?.paymentMethod === 'VA') {
          const orderCheckoutParam = new SubscriptionOrderCheckoutParam()
          await subscription.orderCheckout(orderCheckoutParam)
        }
        await dispatch('getMySubscriptionStatus')
        return true
      } catch (error) {
        return false
      }
    },
    async changeSubscription({commit,dispatch }: { commit: any, dispatch: any }, changeGradeParam: SubscriptionParam) {
      const resp: any = await subscription.patch(changeGradeParam)
      const changeGradeRsp: SubscriptionRsp = resp.data
      commit('setSubscription',resp)
      await dispatch('getMySubscriptionStatus')
      await dispatch('getSubscriptionProfile')
      return changeGradeRsp
    },

    async getSubscriptionProfile({ commit }: { commit: any }) {
      try {
        commit('setSubscriptionProfile', await member.getProfile())
        return true
      } catch (error: any) {
        return false
      }
    }
  },

  namespaced: true,
}
